import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Home, Briefcase, MapPin, Star, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { FullPageLoader } from "../../components/Loaders";
import { toast } from "sonner";
import AddressPickerModal from "../../components/AddressPickerModal";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import { BkPageHeader } from "../../components/blinkit/BkUI";

const LABELS = [
    { key: "Home", Icon: Home },
    { key: "Work", Icon: Briefcase },
    { key: "Other", Icon: MapPin },
];

export default function AddressesPage() {
    // Blinkit-theme tenants get the shared Blinkit header; the page body is the same for every theme.
    const blinkit = useIsBlinkit();
    const [rows, setRows] = useState(null);
    const [editing, setEditing] = useState(null);   // null | "new" | { ...address }

    const load = async () => {
        try {
            const { data } = await api.get("/addresses");
            setRows(data);
        } catch {
            setRows([]);
        }
    };
    useEffect(() => { load(); }, []);

    const setDefault = async (id) => {
        try {
            await api.post(`/addresses/${id}/default`);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this address?")) return;
        try {
            await api.delete(`/addresses/${id}`);
            toast.success("Address removed");
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    if (rows === null) return <FullPageLoader />;

    return (
        <div className="pb-32">
            {blinkit ? (
                <BkPageHeader title="Saved addresses" back="/profile" right={
                    <button data-testid="add-address-btn" onClick={() => setEditing("new")}
                            className="h-9 px-3 rounded-lg bg-primary text-primary-foreground text-[12.5px] font-bold inline-flex items-center gap-1.5">
                        <Plus size={14} /> Add new
                    </button>
                } />
            ) : (
                <header className="px-4 pt-5 pb-4 flex items-center gap-3 bg-card sticky top-0 z-10 border-b border-border">
                    <Link to="/profile" className="p-1 -ml-1"><ArrowLeft size={22} /></Link>
                    <h1 className="text-base font-bold flex-1">Saved addresses</h1>
                    <button
                        data-testid="add-address-btn"
                        onClick={() => setEditing("new")}
                        className="bg-primary text-primary-foreground rounded-xl px-3 py-2 font-bold text-xs inline-flex items-center gap-1.5 shadow-sm"
                    >
                        <Plus size={14} /> Add new
                    </button>
                </header>
            )}

            <div className="p-4 space-y-3 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-3 lg:items-start 2xl:grid-cols-3">
                {rows.length === 0 ? (
                    <div className="py-16 text-center lg:col-span-full">
                        <div className="w-16 h-16 rounded-full bg-muted mx-auto flex items-center justify-center text-muted-foreground">
                            <MapPin size={28} />
                        </div>
                        <p className="font-semibold mt-3" data-testid="addresses-empty">No saved addresses yet</p>
                        <p className="text-xs text-muted-foreground mt-1">Add one to checkout faster next time.</p>
                    </div>
                ) : rows.map((a) => {
                    const Icon = LABELS.find((l) => l.key === a.label)?.Icon || MapPin;
                    return (
                        <div key={a.id} data-testid={`address-${a.id}`} className="bg-card border border-border rounded-2xl p-4">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                                    <Icon size={18} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <p className="font-bold text-sm">{a.label}</p>
                                        {a.is_default && (
                                            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1">
                                                <Star size={10} className="fill-emerald-700" /> Default
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-foreground mt-1 line-clamp-2">
                                        {a.line1}{a.line2 ? `, ${a.line2}` : ""}{a.landmark ? `, near ${a.landmark}` : ""}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {[a.city, a.state, a.pincode].filter(Boolean).join(", ")}
                                    </p>
                                    {a.receiver_name && (
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {a.receiver_name}{a.receiver_mobile ? ` • ${a.receiver_mobile}` : ""}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border text-xs font-semibold">
                                {!a.is_default && (
                                    <button data-testid={`set-default-${a.id}`} onClick={() => setDefault(a.id)} className="text-primary px-2 py-1">
                                        Set as default
                                    </button>
                                )}
                                <button data-testid={`edit-${a.id}`} onClick={() => setEditing(a)} className="text-foreground px-2 py-1 ml-auto">
                                    Edit
                                </button>
                                <button data-testid={`delete-${a.id}`} onClick={() => del(a.id)} className="text-destructive px-2 py-1 inline-flex items-center gap-1">
                                    <Trash2 size={12} /> Delete
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {editing && (
                <AddressPickerModal
                    initial={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}
